import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import WriterInfoCard from '../../template/magazine/WriterInfoCard';
import MarqueeContainer from './MarqueeContainer';
import getAvatarUrl from '../../hooks/useAvatarUrl';

/**
 * ParticipantsMarquee (Scroll-synced)
 * - 스크롤 변화량에 비례해서 좌우로 이동
 * - 무한 루프를 위해 콘텐츠를 반복 배치하고 modulo로 래핑
 * @param {object[]} users
 * @param {boolean} isMobile
 * @param {number} [speed=0.6] - 스크롤 1px 당 가로 이동 픽셀
 * @param {number} [duplicate=3] - 반복 복제 횟수
 */
const ParticipantsMarquee = ({ users, isMobile, speed = 0.6, duplicate = 3 }) => {
  // 랜덤 시드를 한 번만 생성하여 일관된 순서 유지
  const randomSeed = useMemo(() => Math.random(), []);
  
  const shuffledUsers = useMemo(() => {
    const userArray = Array.isArray(users) ? users : [];
    // 사용자 순서 랜덤화 (시드 기반으로 일관성 유지)
    const shuffled = [...userArray];
    // 시드를 기반으로 한 deterministic shuffle
    let seed = randomSeed;
    for (let i = shuffled.length - 1; i > 0; i--) {
      seed = (seed * 9301 + 49297) % 233280; // LCG 알고리즘
      const j = Math.floor((seed / 233280) * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, [users, randomSeed]);

  // Fixed card width that NEVER exceeds viewport
  const FIXED_CARD_WIDTH = isMobile ? 150 : 200;
  const CARD_GAP = isMobile ? 8 : 12;

  // 사용자 카드들을 children으로 생성
  const userCards = useMemo(() => {
    return shuffledUsers.map((user, idx) => {
      // 아바타 URL 처리 유틸리티 함수 사용
      const avatarUrl = getAvatarUrl(user);
      
      return (
        <Box
          key={user.id || idx}
          sx={{
            width: FIXED_CARD_WIDTH,
            height: '100%',
            flexShrink: 0,
            borderLeft: '1px solid',
            borderColor: 'primary.main',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <WriterInfoCard 
            user={user} 
            isMobile={isMobile} 
            fallbackAvatarUrl={avatarUrl} 
          />
        </Box>
      );
    });
  }, [shuffledUsers, FIXED_CARD_WIDTH, isMobile]);

  // 데이터 없을 때 처리
  if (!shuffledUsers || shuffledUsers.length === 0) return null;

  return (
    <MarqueeContainer
      speed={speed}
      duplicate={duplicate}
      height={isMobile ? 140 : 180}
      gap={CARD_GAP}
    >
      {userCards}
    </MarqueeContainer>
  );
};

export default ParticipantsMarquee;


